/* ConsentGate (disclaimer before /login) — text in English and German.
   Rich text: { t, b } segments, b = bold. Same shape in both languages. */
import { ByLang, useL } from "i18n/lang";

export interface Seg { t: string; b?: boolean; }

const en = {
  declined: {
    title: "No worries",
    body: "You'll need to accept the agreement to use the app. Come back anytime — it just takes a moment.",
    again: "Take another look",
  },
  title: "Before you login",
  intro: [
    "This app is an experimental, personal project exploring sleep and dream sensing — built for learning and exploration, not as a medical product.",
    "It plays gentle audio cues during your sleep based on sensor readings. Since this is still experimental, detection won't always be perfect. If you ever feel uncomfortable, you can stop at any time.",
    "This is meant for personal experimentation and isn't a substitute for professional medical or mental health care.",
  ],
  showDetails: "Read full details ▾",
  hideDetails: "Hide full details ▲",
  details: [
    { title: "What this is", body: [{ t: "An independent, personal engineering project exploring EOG/eye-movement sensing, sleep-stage estimation, and audio playback timed to detected REM (dreaming) activity." }] as Seg[] },
    { title: "Not a medical device", body: [
      { t: "This is " }, { t: "not a certified medical device", b: true }, { t: ", and is " },
      { t: "not intended to diagnose, treat, cure, or prevent", b: true }, { t: " any medical or psychological condition." },
    ] as Seg[] },
    { title: "Your own risk, your own choice", body: [{ t: "Use of this app is voluntary, undertaken at your own discretion. If you ever feel distressed or unwell, stop using the app and reach out to a trusted person or professional if needed." }] as Seg[] },
    { title: "No warranty", body: [{ t: 'Provided "as is," without warranty of any kind. The developer(s) accept no liability for outcomes from its use.' }] as Seg[] },
  ],
  checkbox: "I understand this is an experimental personal project, and I'm happy to continue at my own discretion.",
  decline: "Not now",
  accept: "Continue to login →",
};

export type ConsentGateText = typeof en;

const de: ConsentGateText = {
  declined: {
    title: "Kein Problem",
    body: "Um die App zu nutzen, musst du der Vereinbarung zustimmen. Komm jederzeit wieder – es dauert nur einen Moment.",
    again: "Noch mal ansehen",
  },
  title: "Bevor du dich anmeldest",
  intro: [
    "Diese App ist ein experimentelles, persönliches Projekt, das Schlaf- und Traumerkennung erforscht – entwickelt zum Lernen und Ausprobieren, nicht als medizinisches Produkt.",
    "Sie spielt während deines Schlafs sanfte Audio-Hinweise ab, basierend auf Sensormesswerten. Da sie noch experimentell ist, wird die Erkennung nicht immer perfekt sein. Wenn du dich unwohl fühlst, kannst du jederzeit aufhören.",
    "Sie ist für persönliches Experimentieren gedacht und ersetzt keine professionelle medizinische oder psychologische Betreuung.",
  ],
  showDetails: "Alle Details lesen ▾",
  hideDetails: "Details ausblenden ▲",
  details: [
    { title: "Was das ist", body: [{ t: "Ein unabhängiges, persönliches Technikprojekt, das EOG-/Augenbewegungserkennung, die Schätzung von Schlafphasen und Audiowiedergabe passend zu erkannter REM-(Traum-)Aktivität erforscht." }] },
    { title: "Kein Medizinprodukt", body: [
      { t: "Dies ist " }, { t: "kein zertifiziertes Medizinprodukt", b: true }, { t: " und " },
      { t: "nicht dazu bestimmt, medizinische oder psychische Erkrankungen zu diagnostizieren, zu behandeln, zu heilen oder ihnen vorzubeugen", b: true }, { t: "." },
    ] },
    { title: "Eigenes Risiko, eigene Entscheidung", body: [{ t: "Die Nutzung dieser App ist freiwillig und erfolgt nach eigenem Ermessen. Wenn du dich belastet oder unwohl fühlst, beende die Nutzung und wende dich bei Bedarf an eine Vertrauensperson oder eine Fachkraft." }] },
    { title: "Keine Gewährleistung", body: [{ t: "Bereitgestellt „wie besehen“, ohne jegliche Gewährleistung. Die Entwickler übernehmen keine Haftung für Folgen der Nutzung." }] },
  ],
  checkbox: "Ich verstehe, dass dies ein experimentelles, persönliches Projekt ist, und fahre gern nach eigenem Ermessen fort.",
  decline: "Jetzt nicht",
  accept: "Weiter zur Anmeldung →",
};

export const consentGateText: ByLang<ConsentGateText> = { en, de };
export const useConsentGateText = () => useL(consentGateText);
